import type {
  AccessArea,
  AccessAssignment,
  AccessKeySetting,
  WorkbookAccessSettings,
  WorkingAccess,
} from "./types";

// What the page is editing: the live rows, with a setting for every sheet it lists.
export const workingFromSettings = (
  settings: WorkbookAccessSettings,
  useDraft = true,
): WorkingAccess => {
  const saved = new Map(
    (useDraft
      ? (settings.draft?.key_settings ?? settings.live.key_settings)
      : settings.live.key_settings
    ).map((setting) => [setting.key, setting]),
  );
  return {
    assignments: useDraft
      ? (settings.draft?.assignments ?? settings.live.assignments)
      : settings.live.assignments,
    keySettings: settings.access_keys.map(
      (area) =>
        saved.get(area.key) ?? {
          key: area.key,
          name: area.name,
          team_eligible: area.team_eligible,
        },
    ),
  };
};

const assignmentId = (row: AccessAssignment) =>
  `${row.user_id}:${row.full_workbook_access ? "*" : row.access_key}`;

// Fields in a fixed order: the server's jsonb hands keys back sorted, so two
// copies of the same access still compare equal.
const canonicalAssignments = (rows: AccessAssignment[]) =>
  [...rows]
    .map((row) => ({
      user_id: row.user_id,
      full_workbook_access: row.full_workbook_access,
      access_key: row.full_workbook_access ? null : row.access_key,
      access_level: row.access_level,
      approval_access: !row.full_workbook_access && row.approval_access,
      notify_pack_questions: row.notify_pack_questions,
    }))
    .sort((a, b) => assignmentId(a).localeCompare(assignmentId(b)));

const canonicalKeys = (settings: AccessKeySetting[]) =>
  [...settings]
    .map((setting) => ({
      key: setting.key,
      name: setting.name.trim(),
      team_eligible: setting.team_eligible,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const sameWorking = (a: WorkingAccess, b: WorkingAccess) =>
  JSON.stringify(canonicalAssignments(a.assignments)) ===
    JSON.stringify(canonicalAssignments(b.assignments)) &&
  JSON.stringify(canonicalKeys(a.keySettings)) ===
    JSON.stringify(canonicalKeys(b.keySettings));

export const assignmentsFor = (rows: AccessAssignment[], userId: string) =>
  rows.filter((row) => row.user_id === userId);

// Everyone who can open a sheet: whole-workbook holders first, then people given
// the sheet itself. A whole-workbook approver's sheet row doesn't list them twice.
export const sheetHolderIds = (rows: AccessAssignment[], key: string) => {
  const whole = rows.filter((row) => row.full_workbook_access);
  const sheet = rows.filter(
    (row) => !row.full_workbook_access && row.access_key === key,
  );
  return Array.from(new Set([...whole, ...sheet].map((row) => row.user_id)));
};

export const replacePersonAssignments = (
  working: WorkingAccess,
  userId: string,
  rows: AccessAssignment[],
): WorkingAccess => ({
  ...working,
  assignments: [
    ...working.assignments.filter((row) => row.user_id !== userId),
    ...rows,
  ],
});

export const changedUserIds = (a: WorkingAccess, b: WorkingAccess) => {
  const ids = new Set(
    [...a.assignments, ...b.assignments].map((row) => row.user_id),
  );
  return Array.from(ids).filter(
    (id) =>
      JSON.stringify(
        canonicalAssignments(assignmentsFor(a.assignments, id)),
      ) !==
      JSON.stringify(canonicalAssignments(assignmentsFor(b.assignments, id))),
  );
};

export const areaName = (
  key: string | null,
  keySettings: AccessKeySetting[],
  areas: AccessArea[],
) =>
  keySettings.find((setting) => setting.key === key)?.name ??
  areas.find((area) => area.key === key)?.name ??
  key ??
  "";
